// Dropdown trigger for the export endpoints. The job-sheet path
// needs a job id, so when the user clicks "Workshop Job Sheet" we
// prompt them to pick from a small popup; cutting list and XLSX
// are one-click.
import { useState } from "react";
import type { JobDto } from "../../../../../../shared/types/production-schedule";
import { apiDownload } from "../../../../lib/api";

interface Props {
  jobs: JobDto[];
}

export function ExportMenu({ jobs }: Props) {
  const [open, setOpen] = useState(false);
  const [picking, setPicking] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);

  const reportFailure = (action: string, err: unknown) => {
    const message = err instanceof Error ? err.message : String(err);
    // Plain alert keeps the surface tiny; the audit log captures the
    // server-side error separately. Swap for a toast once the hub
    // grows a notification primitive.
    window.alert(`${action} failed: ${message}`);
  };

  const downloadCutting = async () => {
    setBusy("cutting");
    setOpen(false);
    try {
      await apiDownload(
        "/api/tools/production-schedule/reports/cutting-list.pdf",
        "cutting-list.pdf",
      );
    } catch (e) {
      reportFailure("Cutting list export", e);
    } finally {
      setBusy(null);
    }
  };
  const downloadXlsx = async () => {
    setBusy("xlsx");
    setOpen(false);
    try {
      await apiDownload(
        "/api/tools/production-schedule/reports/schedule.xlsx",
        "production-schedule.xlsx",
      );
    } catch (e) {
      reportFailure("Schedule export", e);
    } finally {
      setBusy(null);
    }
  };
  const downloadJobSheet = async (jobId: number, includeQA: boolean, includeDates: boolean) => {
    setBusy(`job-${jobId}`);
    setPicking(false);
    try {
      const qs = new URLSearchParams();
      if (!includeQA) qs.set("includeQA", "false");
      if (!includeDates) qs.set("includeDates", "false");
      const url = `/api/tools/production-schedule/reports/job-sheet/${jobId}.pdf${qs.toString() ? `?${qs.toString()}` : ""}`;
      await apiDownload(url, "job-sheet.pdf");
    } catch (e) {
      reportFailure("Job sheet export", e);
    } finally {
      setBusy(null);
    }
  };

  return (
    <div style={{ position: "relative" }}>
      <button
        type="button"
        className="bsp-btn bsp-btn-secondary"
        onClick={() => setOpen((v) => !v)}
      >
        Export ▾
      </button>
      {open && (
        <div style={{ position: "absolute", right: 0, top: 40, background: "var(--bsp-surface)", border: "1px solid var(--bsp-border)", borderRadius: 4, boxShadow: "var(--bsp-shadow-pop)", padding: 6, zIndex: 100, minWidth: 230 }}>
          <MenuItem label="Workshop Job Sheet (PDF)" onClick={() => { setOpen(false); setPicking(true); }} />
          <MenuItem
            label={busy === "cutting" ? "Downloading…" : "Cutting List (PDF)"}
            onClick={() => void downloadCutting()}
          />
          <MenuItem
            label={busy === "xlsx" ? "Downloading…" : "Schedule (Excel)"}
            onClick={() => void downloadXlsx()}
          />
        </div>
      )}
      {picking && (
        <JobSheetPicker
          jobs={jobs}
          onCancel={() => setPicking(false)}
          onPick={(jobId, includeQA, includeDates) => void downloadJobSheet(jobId, includeQA, includeDates)}
        />
      )}
    </div>
  );
}

function JobSheetPicker({
  jobs,
  onCancel,
  onPick,
}: {
  jobs: JobDto[];
  onCancel: () => void;
  onPick: (jobId: number, includeQA: boolean, includeDates: boolean) => void;
}) {
  const [jobId, setJobId] = useState<number | "">("");
  const [includeQA, setIncludeQA] = useState(true);
  const [includeDates, setIncludeDates] = useState(true);
  return (
    <div className="modal-bg" onClick={(e) => { if (e.target === e.currentTarget) onCancel(); }}>
      <div className="modal" style={{ width: 460 }}>
        <div className="modal-header">
          <h2>Export Workshop Job Sheet</h2>
          <button className="modal-close" onClick={onCancel}>×</button>
        </div>
        <div className="modal-body">
          <p style={{ fontSize: 12, color: "var(--bsp-fg-muted)" }}>
            Pick a job and choose which columns to include. Defaults match the prototype.
          </p>
          <select
            value={jobId}
            onChange={(e) => setJobId(e.target.value ? Number(e.target.value) : "")}
            style={{ width: "100%", padding: 9, fontSize: 13, border: "1px solid var(--bsp-border-strong)", borderRadius: 4, marginBottom: 14 }}
          >
            <option value="">— Choose a job —</option>
            {jobs.map((j) => (
              <option key={j.id} value={j.id}>{j.jobNumber} · {j.name}</option>
            ))}
          </select>
          <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, fontWeight: 600, marginBottom: 6 }}>
            <input type="checkbox" checked={includeQA} onChange={(e) => setIncludeQA(e.target.checked)} />
            Include QA sign-off column
          </label>
          <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, fontWeight: 600 }}>
            <input type="checkbox" checked={includeDates} onChange={(e) => setIncludeDates(e.target.checked)} />
            Include cut/weld dates
          </label>
        </div>
        <div className="modal-footer">
          <button className="btn-cancel" onClick={onCancel}>Cancel</button>
          <button
            className="btn-submit"
            disabled={typeof jobId !== "number"}
            onClick={() => { if (typeof jobId === "number") onPick(jobId, includeQA, includeDates); }}
          >
            Export PDF
          </button>
        </div>
      </div>
    </div>
  );
}

function MenuItem({ label, onClick }: { label: string; onClick(): void }) {
  return (
    <div
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") onClick(); }}
      style={{ padding: "9px 12px", cursor: "pointer", borderRadius: 2, fontSize: 12, fontWeight: 600, color: "var(--bsp-fg)" }}
      onMouseOver={(e) => { e.currentTarget.style.background = "var(--bsp-blue-pale)"; }}
      onMouseOut={(e) => { e.currentTarget.style.background = ""; }}
    >
      {label}
    </div>
  );
}
